import type { ClaudeChannelApproval } from "common";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { useClaudeChannelApprovals } from "@via/api/claude-channel.api";

const outcome = (a: ClaudeChannelApproval): string => {
  if (a.answered === "drawer") {
    return a.verdict === "deny"
      ? "Answered here: denied"
      : "Answered here: allowed";
  }
  return a.resolvedAt ? "Outcome unknown" : "Waiting for an answer";
};

/** The caller's own approval requests, newest first. */
export const ApprovalHistory = ({ onBack }: { onBack: () => void }) => {
  const { data = [], isLoading, isError } = useClaudeChannelApprovals();
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2">
        <button
          type="button"
          aria-label="Back to sessions"
          onClick={onBack}
          className="rounded p-1 text-gray-500 hover:bg-gray-100">
          <ArrowLeftIcon className="h-4 w-4" />
        </button>
        <p className="text-sm font-semibold text-gray-900">Approval history</p>
      </div>
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading && <p className="text-sm text-gray-500">Loading...</p>}
        {isError && (
          <p className="text-sm text-red-700">
            The history could not be loaded.
          </p>
        )}
        {!isLoading && !isError && data.length === 0 && (
          <p className="text-sm text-gray-500">No approval requests yet.</p>
        )}
        <ul className="space-y-2" aria-label="Approval history">
          {data.map(a => (
            <li
              key={a.id}
              data-testid="claude-approval-history-row"
              className="rounded-md border border-gray-200 bg-white p-2">
              <div className="flex items-center justify-between gap-2">
                <p className="truncate text-sm font-medium text-gray-900">
                  {a.toolName}
                </p>
                <p className="shrink-0 text-xs text-gray-500">
                  {new Date(a.createdAt).toLocaleString()}
                </p>
              </div>
              {a.description && (
                <p className="truncate text-xs text-gray-600">
                  {a.description}
                </p>
              )}
              <p className="mt-1 text-xs font-medium text-gray-700">
                {outcome(a)}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
